import { Button, Modal, SvgExternalLink } from '@loomhq/lens';
import React from 'react';
import { useDispatch } from 'react-redux';

import * as analytics from 'utilities/analytics';
import * as intercom from 'utilities/intercom';

import RecordVideoImg from 'img/workspace-onboarding/record-a-video.png';

import DashboardOnboardingCard, {
  DashboardOnboardingSecondaryAction,
} from '../..';
import {
  toggleLoomDownloadModal,
  toggleRecordToNonDefaultWorkspaceModal,
} from '../../../../../../actions/videosDashboard';
import { DASHBOARDING_ONBOARDING_CARD_CTA_CLICKED } from '../../../../../../constants/events';
import {
  useGetDefaultWorkspace,
  useGetSelectedWorkspace,
  useGetUserRoleForSelectedWorkspace,
} from '../../../../../../hooks/workspace';
import { requestExtensionOpenIfInstalled } from '../../../../../../utilities/extension';
import { ORG_ROLE_ADMIN } from '../../../../../../../../shared/constants/organizationRoles';
import { RECORD_VIDEO_CARD_KEY } from '../../../../../../../../shared/constants/workspaceOnboarding';
import {
  PRIVATE_WORKSPACE,
  SHARED_WORKSPACE,
  TEAM_WORKSPACE,
} from '../../../../../../../../shared/constants/workspaces';

const SUPPORT_ARTICLE_LINK =
  'https://support.loom.com/hc/en-us/articles/360007405538-How-to-record-with-Loom-all-platforms-';

export const filterFunction = (
  library,
  isAdmin,
  libraryVideoCounts,
  isExistingUser
) => {
  if (isExistingUser && !isAdmin) {
    const personalVideoExists = libraryVideoCounts[PRIVATE_WORKSPACE] === 0;

    return personalVideoExists;
  }

  return true;
};

// modal related constants
const PERSONAL_LIBRARY_TOUR = 116450;
const SHARED_LIBRARY_TOUR = 116454;
const TEAM_LIBRARY_TOUR = 116458;

const MODAL_SUPPORT_ARTICLE_LINK =
  ' https://support.loom.com/hc/en-us/articles/360007611558-Recording-into-your-preferred-workspace';

export const RecordToNonDefaultWorkspaceModal = ({
  isOpen,
  onCloseClick,
  library,
}) => {
  let tourID = null;

  switch (library) {
    case TEAM_WORKSPACE:
      tourID = TEAM_LIBRARY_TOUR;
      break;
    case SHARED_WORKSPACE:
      tourID = SHARED_LIBRARY_TOUR;
      break;
    case PRIVATE_WORKSPACE:
      tourID = PERSONAL_LIBRARY_TOUR;
      break;
    default:
      break;
  }

  const tourHandler = () => {
    onCloseClick();

    intercom.startTour(tourID);
  };

  return (
    <Modal
      isOpen={isOpen}
      onCloseClick={onCloseClick}
      onBackgroundClick={onCloseClick}
      title="⚠️ You’re not in your default workspace"
      mainButton={
        <Button variant="primary" onClick={tourHandler}>
          Show me how
        </Button>
      }
      secondaryButton={
        <Button
          htmlTag="a"
          icon={<SvgExternalLink />}
          href={MODAL_SUPPORT_ARTICLE_LINK}
          target="_blank"
          rel="noreferrer noopener"
        >
          Learn how
        </Button>
      }
    >
      You can only record videos to your default workspace. Change your default
      workspace to record and save a video here.
    </Modal>
  );
};

const RecordVideoCard = ({ cardConfig }) => {
  const selectedWorkspace = useGetSelectedWorkspace();
  const defaultWorkspace = useGetDefaultWorkspace();
  const userRole = useGetUserRoleForSelectedWorkspace();
  const dispatch = useDispatch();

  if (!userRole) {
    return null;
  }

  const isExistingUser = selectedWorkspace.was_loom_user_before_membership;

  const isDefaultWorkspace = selectedWorkspace?.id === defaultWorkspace?.id;

  const isUserAdminOfWorkspace = userRole === ORG_ROLE_ADMIN;

  const header = 'Record a video';

  const description = 'Get started by creating your first video.';

  const completeMessage =
    isUserAdminOfWorkspace && isExistingUser
      ? 'Nicely done! Share your video or add it to the Team Library.'
      : 'Nicely done! You recorded your first video.';

  const secondaryAction = (
    <DashboardOnboardingSecondaryAction
      icon={<SvgExternalLink />}
      onClick={() => {
        window.open(SUPPORT_ARTICLE_LINK);
        analytics.track(DASHBOARDING_ONBOARDING_CARD_CTA_CLICKED, {
          type: 'secondary',
          card: RECORD_VIDEO_CARD_KEY,
        });
      }}
    >
      Learn how
    </DashboardOnboardingSecondaryAction>
  );

  const primaryActionClickHandler = isDefaultWorkspace
    ? () => {
        requestExtensionOpenIfInstalled(
          'dashboard-onboarding-card',
          success => {
            if (!success) {
              dispatch(toggleLoomDownloadModal(true));
            }
          }
        );
      }
    : () => dispatch(toggleRecordToNonDefaultWorkspaceModal(true));

  const onPrimaryAction = () => {
    primaryActionClickHandler();

    analytics.track(DASHBOARDING_ONBOARDING_CARD_CTA_CLICKED, {
      type: 'primary',
      card: RECORD_VIDEO_CARD_KEY,
    });
  };

  const primaryAction = (
    <Button variant="record" onClick={onPrimaryAction}>
      Start recording
    </Button>
  );

  const centerpiece = <img src={RecordVideoImg} />;

  return (
    <>
      <DashboardOnboardingCard
        cardKey={RECORD_VIDEO_CARD_KEY}
        cardConfig={cardConfig}
        heading={header}
        description={description}
        centerpiece={centerpiece}
        secondaryAction={secondaryAction}
        primaryAction={primaryAction}
        completeMessage={completeMessage}
        showClose={false}
        showCloseCompact={false}
      />
    </>
  );
};

export default RecordVideoCard;
